import type { BufferGeometry } from 'three'
import { extrusao, torno } from '../componentes/malhasProcedurais'
import type { NivelQualidade } from '../ambiente/qualidadeGrafica'
import {
  contornoBolsa,
  contornoBraco,
  contornoGarfo,
  contornoPonteira,
  type Ponto2,
} from './geometriaAlargador'

/** Espessura (em z) do corpo do braço, sem o chanfro. */
export const ESPESSURA_BRACO = 0.044
/** A ponteira é mais grossa que o braço: cobre a emenda e lê como peça à parte. */
export const ESPESSURA_PONTEIRA = 0.054
export const ESPESSURA_PLACA_GARFO = 0.012
export const ESPESSURA_BOLSA = 0.003

interface Detalhe {
  /** Passos por curva dos contornos. */
  readonly passosContorno: number
  /** Segmentos do chanfro das extrusões. */
  readonly segmentosChanfro: number
  /** Segmentos radiais dos corpos torneados. */
  readonly segmentosTorno: number
}

const DETALHE: Record<NivelQualidade, Detalhe> = {
  alta: { passosContorno: 14, segmentosChanfro: 3, segmentosTorno: 40 },
  media: { passosContorno: 10, segmentosChanfro: 2, segmentosTorno: 28 },
  baixa: { passosContorno: 6, segmentosChanfro: 1, segmentosTorno: 16 },
}

/**
 * Cilindro hidráulico: tampas chanfradas e um sulco de anel de vedação perto
 * da frente. O sulco é o que faz o tubo laranja ler como peça usinada e não
 * como cano.
 */
const PERFIL_CILINDRO: Ponto2[] = [
  [-0.4, 0],
  [-0.4, 0.05],
  [-0.396, 0.058],
  [-0.388, 0.062],
  [-0.17, 0.062],
  [-0.166, 0.058],
  [-0.16, 0.058],
  [-0.156, 0.062],
  [-0.134, 0.062],
  [-0.124, 0.056],
  [-0.12, 0.034],
  [-0.12, 0],
]

/**
 * Carcaça do motor com aletas de refrigeração e, atrás dela, o anel de
 * comando giratório e a manopla emborrachada com anéis de pega — um torno só,
 * do motor até a tampa traseira.
 */
function perfilModuloTraseiro(): Ponto2[] {
  const pontos: Ponto2[] = [[-0.4, 0], [-0.4, 0.07], [-0.404, 0.076]]
  // aletas: 6 cristas entre -0.41 e -0.55
  for (let i = 0; i < 6; i++) {
    const x = -0.41 - i * 0.024
    pontos.push([x, 0.078], [x - 0.008, 0.078], [x - 0.012, 0.071], [x - 0.02, 0.071], [x - 0.024, 0.078])
  }
  pontos.push(
    [-0.556, 0.076],
    [-0.562, 0.064],
    [-0.57, 0.046],
    // anel de comando giratório (proporcional)
    [-0.574, 0.046],
    [-0.578, 0.048],
    [-0.622, 0.048],
    [-0.626, 0.044],
    [-0.632, 0.03],
  )
  // manopla com anéis de pega
  for (let i = 0; i < 5; i++) {
    const x = -0.638 - i * 0.02
    pontos.push([x, 0.03], [x - 0.006, 0.033], [x - 0.012, 0.033], [x - 0.016, 0.03])
  }
  pontos.push([-0.74, 0.03], [-0.748, 0.024], [-0.756, 0.012], [-0.758, 0])
  // montado de frente para trás; o torno quer x crescente
  return pontos.reverse()
}

export interface MalhasAlargador {
  readonly braco: BufferGeometry
  readonly ponteira: BufferGeometry
  readonly bolsa: BufferGeometry
  readonly placaGarfo: BufferGeometry
  readonly cilindro: BufferGeometry
  readonly moduloTraseiro: BufferGeometry
}

const CACHE = new Map<NivelQualidade, MalhasAlargador>()

/**
 * Geometrias do alargador para um nível de qualidade. Montadas uma vez por
 * nível e compartilhadas por todos os alargadores da cena (a elevação de
 * painel mostra dois ao mesmo tempo); nunca são descartadas — são poucas e
 * pequenas, e recriar a cada montagem custaria mais que mantê-las.
 */
export function malhasAlargador(nivel: NivelQualidade): MalhasAlargador {
  const existente = CACHE.get(nivel)
  if (existente) return existente

  const { passosContorno, segmentosChanfro, segmentosTorno } = DETALHE[nivel]
  const malhas: MalhasAlargador = {
    braco: extrusao(contornoBraco(passosContorno), ESPESSURA_BRACO, 0.005, segmentosChanfro),
    ponteira: extrusao(contornoPonteira(), ESPESSURA_PONTEIRA, 0.003, segmentosChanfro),
    bolsa: extrusao(contornoBolsa(), ESPESSURA_BOLSA, 0.001, 1),
    placaGarfo: extrusao(contornoGarfo(passosContorno), ESPESSURA_PLACA_GARFO, 0.003, segmentosChanfro),
    cilindro: torno(PERFIL_CILINDRO, segmentosTorno),
    moduloTraseiro: torno(perfilModuloTraseiro(), segmentosTorno),
  }
  CACHE.set(nivel, malhas)
  return malhas
}
